import { MediaModel } from 'src/app/common/media.model';
import { WalletTemplateField } from 'src/app/common/wallet-template-field.model';
import { GoogleWalletPassStyle } from 'src/app/common/wallet-passstyle-enum';
import { WalletGoogleField } from 'src/app/common/wallet-template-enums';

export type GoogleSaveValidationFieldKey =
  | 'googleProgramName'
  | 'googleTopRowHeader'
  | 'googleTopRowSubHeader'
  | 'googleBarcodeValue'
  | 'googleDynamicFields'
  | 'googleLocalizedTitle'
  | 'googleImages';

export interface GoogleWalletSaveValidationContext {
  googleWalletFields: WalletTemplateField[];
  mediaModel: MediaModel;
  walletStyle: GoogleWalletPassStyle;
  tabIndexContent: number;
  tabIndexAppearance: number;
}

export interface GoogleSaveValidationError {
  tabIndex: number;
  fieldKey: GoogleSaveValidationFieldKey;
  messageKey: string;
}

export function validateGoogleWalletForSave(context: GoogleWalletSaveValidationContext): GoogleSaveValidationError[] {
  const errors: GoogleSaveValidationError[] = [];

  if (!hasGoogleRequiredImages(context)) {
    errors.push({
      tabIndex: context.tabIndexAppearance,
      fieldKey: 'googleImages',
      messageKey: 'admin.web.wallet-validation-google-images-required',
    });
  }

  switch (context.walletStyle) {
    case GoogleWalletPassStyle.Generic:
      if (!hasGoogleFieldWithValue(context, WalletGoogleField.ProgramName)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleProgramName',
          messageKey: 'admin.web.wallet-validation-google-program-name-required',
        });
      }

      if (!hasGoogleFieldWithValue(context, WalletGoogleField.TopRowHeader)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleTopRowHeader',
          messageKey: 'admin.web.wallet-validation-google-top-row-header-required',
        });
      }

      if (!hasGoogleFieldWithValue(context, WalletGoogleField.TopRowSubheader)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleTopRowSubHeader',
          messageKey: 'admin.web.wallet-validation-google-top-row-subheader-required',
        });
      }

      if (!hasGoogleFieldWithValue(context, WalletGoogleField.BarcodeValue)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleBarcodeValue',
          messageKey: 'admin.web.wallet-validation-google-barcode-value-required',
        });
      }

      if (!hasAnyGoogleDynamicField(context)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleDynamicFields',
          messageKey: 'admin.web.wallet-validation-google-dynamic-field-required',
        });
      }
      break;
    case GoogleWalletPassStyle.Offer:
      if (!hasGoogleFieldWithValue(context, WalletGoogleField.LocalizedTitle)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleLocalizedTitle',
          messageKey: 'admin.web.wallet-validation-google-localized-title-required',
        });
      }

      if (!hasGoogleFieldWithValue(context, WalletGoogleField.BarcodeValue)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleBarcodeValue',
          messageKey: 'admin.web.wallet-validation-google-barcode-value-required',
        });
      }
      break;
    case GoogleWalletPassStyle.LoyaltyCard:
      if (!hasGoogleFieldWithValue(context, WalletGoogleField.ProgramName)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleProgramName',
          messageKey: 'admin.web.wallet-validation-google-program-name-required',
        });
      }

      if (!hasGoogleFieldWithValue(context, WalletGoogleField.BarcodeValue)) {
        errors.push({
          tabIndex: context.tabIndexContent,
          fieldKey: 'googleBarcodeValue',
          messageKey: 'admin.web.wallet-validation-google-barcode-value-required',
        });
      }
      break;
    default:
      break;
  }

  return errors;
}

export function isGoogleValidationFieldSatisfied(
  fieldKey: GoogleSaveValidationFieldKey,
  context: GoogleWalletSaveValidationContext,
): boolean {
  switch (fieldKey) {
    case 'googleProgramName':
      return hasGoogleFieldWithValue(context, WalletGoogleField.ProgramName);
    case 'googleTopRowHeader':
      return hasGoogleFieldWithValue(context, WalletGoogleField.TopRowHeader);
    case 'googleTopRowSubHeader':
      return hasGoogleFieldWithValue(context, WalletGoogleField.TopRowSubheader);
    case 'googleBarcodeValue':
      return hasGoogleFieldWithValue(context, WalletGoogleField.BarcodeValue);
    case 'googleDynamicFields':
      return hasAnyGoogleDynamicField(context);
    case 'googleLocalizedTitle':
      return hasGoogleFieldWithValue(context, WalletGoogleField.LocalizedTitle);
    case 'googleImages':
      return hasGoogleRequiredImages(context);
    default:
      return true;
  }
}

function hasGoogleRequiredImages(context: GoogleWalletSaveValidationContext): boolean {
  return !!context.mediaModel?.walletGoogleLogoUID && !!context.mediaModel?.walletGoogleImageUID;
}

function hasGoogleFieldWithValue(
  context: GoogleWalletSaveValidationContext,
  fieldPosition: WalletGoogleField,
): boolean {
  const field = (context.googleWalletFields ?? []).find(f => f.googleField === fieldPosition);

  if(field){
    return true;
  }

  return false;
}

function hasAnyGoogleDynamicField(context: GoogleWalletSaveValidationContext): boolean {
  const dynamicFields = [
    WalletGoogleField.Row1Left,
    WalletGoogleField.Row1Middle,
    WalletGoogleField.Row1Right,
    WalletGoogleField.Row2Left,
    WalletGoogleField.Row2Middle,
    WalletGoogleField.Row2Right,
    WalletGoogleField.Row3Left,
    WalletGoogleField.Row3Middle,
    WalletGoogleField.Row3Right,
  ];

  return (context.googleWalletFields ?? []).some(field => {
    if (!dynamicFields.includes(field.googleField)) {
      return false;
    }

    const headerValue = String(field.label ?? '').trim();
    const textValue = String(field.defaultValueSourceField ?? field.sourceField ?? '').trim();

    return headerValue.length > 0 && textValue.length > 0;
  });
}
